import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'
import { ServiceInternalFileUpload } from 'config/apiServiceFile'

import { normalizeApprovalListPaginationCursors } from 'features/AdsManagement/helpers/normalizeApprovalListCursor'
import {
  exportTaxInvoiceSubmissions,
  getTaxInvoiceSubmissions,
  uploadTaxInvoice,
  type TaxInvoiceListParamsType,
} from 'features/AdsManagement/services/taxInvoice'
import { toastSuccess } from 'utils/toast'

import {
  setIsLoading,
  setIsUploading,
  setListFetchStatus,
  setPagination,
  setSubmissions,
  SLICE_NAME,
} from './slice'

const FAILED_UPLOAD_MESSAGE = 'Gagal mengupload file.'

export const fetchTaxInvoiceSubmissions = createAsyncThunk(
  `${SLICE_NAME}/fetchTaxInvoiceSubmissions`,
  async ({ params }: { params: TaxInvoiceListParamsType }, { rejectWithValue, dispatch }) => {
    dispatch(setIsLoading(true))
    dispatch(setListFetchStatus('loading'))
    try {
      const response = await getTaxInvoiceSubmissions(params)
      dispatch(setSubmissions(response.data.data.tax_invoice_submissions))
      dispatch(setPagination(normalizeApprovalListPaginationCursors(response.data.pagination)))
      dispatch(setIsLoading(false))
      dispatch(setListFetchStatus('success'))
      return true
    } catch (error) {
      dispatch(setIsLoading(false))
      dispatch(setListFetchStatus('error'))
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const uploadTaxInvoiceFile = createAsyncThunk(
  `${SLICE_NAME}/uploadTaxInvoiceFile`,
  async (
    {
      submissionId,
      sellerId,
      file,
      createdBy,
    }: { submissionId: string; sellerId: string; file: File; createdBy: string },
    { rejectWithValue, dispatch },
  ) => {
    dispatch(setIsUploading(true))
    try {
      const data: { file_input: File; created_by: string; external_id: number } = {
        file_input: file,
        created_by: createdBy,
        external_id: Number(sellerId),
      }
      const results = await ServiceInternalFileUpload([
        {
          typeId: 'seller',
          data,
        },
      ])

      const result = results[0]
      if (result.error) {
        throw new Error(result.message || FAILED_UPLOAD_MESSAGE)
      }

      const fileUrl = result.files?.fileUrl || ''
      if (!fileUrl) {
        throw new Error(FAILED_UPLOAD_MESSAGE)
      }

      await uploadTaxInvoice({
        submission_id: submissionId,
        seller_id: sellerId,
        file_url: fileUrl,
      })
      dispatch(setIsUploading(false))
      toastSuccess('Faktur pajak berhasil diupload')
      return true
    } catch (error) {
      dispatch(setIsUploading(false))
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const exportTaxInvoiceData = createAsyncThunk(
  `${SLICE_NAME}/exportTaxInvoiceData`,
  async (
    {
      params,
    }: {
      params: Omit<
        TaxInvoiceListParamsType,
        'page_cursor' | 'page_size' | 'sort_by' | 'order_direction'
      >
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await exportTaxInvoiceSubmissions(params)
      return response.data.file_url
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)
